import { roleHelpers } from '@/services/utils';
import { getPinia } from '@/createPiniaSingleton';
import { useReportFiltersStore } from '@/stores/reports/useReportFiltersStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import ReportFilterPanel from '../components/ReportFilterPanel.vue';
import { mountReportVue, withReportFilterPanelFeature } from './reportFilterPanelHost';
import { loadPanelContext, onFiltersChange } from './reportFilterPanelSync';

/**
 * The generic engine's filter sidebar. With the `reportFilterPanel` feature it hosts
 * `ReportFilterPanel`, driven by `showFilters`; without it, the legacy template runs
 * unchanged. Delete `linkLegacy` and `views/reportFilter.html` once the feature is on
 * for every firm (sc-48496).
 */
angular.module('leanlaw').directive('reportFilter', ['$injector', '$compile', 'filterService', 'userService', 'leanLawAuth', 'leanLawApi', 'matterTagsService', 'lists', 'reportFilterService', function ($injector, $compile, filterService, userService, leanLawAuth, leanLawApi, matterTagsService, lists, reportFilterService) {
  function linkLegacy($scope) {
    const {
      handleError
    } = useSnackbarHandler();
    // Same store the standard reports' commonFilter writes, which is what keeps
    // "Show Matter Involving" in step across the two report families.
    const reportFilters = useReportFiltersStore(getPinia());
    userService.me().then(me => {
      $scope.me = me;
      matterTagsService.getMatterTags().then(mattertags => {
        $scope.matterTags = mattertags;
      });
      userService.list({
        status: 'all'
      }).then(users => {
        const lockByDefault = me.role === 'lead' || me.role === 'timekeeper' || me.role === 'paralegal';
        if (me.permissions.matters.autoAssign || !lockByDefault) {
          $scope.accounts = users;
        } else {
          $scope.lockAccountSelection = true;
          $scope.accounts = [me];
        }
        $scope.accounts = filterService.formatDisabledUser($scope.accounts);
        // Retrieve the globally saved "Show Matter Involving" account filter state
        const savedGlobalReportsScopeAccount = reportFilters.filters.scopeAccount;
        const isScopeAccountChanged = reportFilters.scopeAccountTouched;
        // Check if the global filter has been previously set by user
        if (isScopeAccountChanged) {
          if (savedGlobalReportsScopeAccount) {
            // Find the matching account in the current user's available accounts list
            const matchingAccount = $scope.accounts.find(a => a.id === savedGlobalReportsScopeAccount.id);
            if (matchingAccount) {
              // Apply the saved account filter to the current view
              $scope.filter.scopeAccount = matchingAccount;
              return;
            }
          } else {
            // If the global filter was explicitly set to null, apply that preference
            $scope.filter.scopeAccount = null;
            return;
          }
        }
        if (!roleHelpers.isOperatorOrAccountantRole(me.role)) {
          $scope.filter.scopeAccount = me; // Default to current user for most roles
        } else {
          $scope.filter.scopeAccount = null; // Default to no filter for operators/accountants
        }
      });
      if (me.company.settings.trustEnabled) {
        $scope.trusts = [];
        $scope.trusts.push(me.company.settings?.trust?.operating);
        me.company.settings?.trust?.accounts.map(a => $scope.trusts.push({
          id: a.bank.id,
          name: a.bank.name
        }));
        $scope.filter.trustAccount = $scope.trusts[0];
      }
      $scope.showTimekeeper = me.permissions.time.createOthers;
    }, e => handleError(e));
    $scope.distributionStatusOptions = lists.distributionStatuses;
    $scope.options = reportFilterService.options();
    leanLawApi.listPaymentDistributions().then(list => {
      $scope.distributions = list.distributions;
    });
    $scope.distributionChange = function () {
      const {
        distributionId,
        distributionStatus
      } = $scope.filter;
      $scope.show.billed = !distributionId && !distributionStatus && $scope.show.billed;
    };
    $scope.scopeChange = function (prop) {
      // Store filter state to maintain consistency between reports
      reportFilterService.setFilterState({
        primary: $scope.filter.primary,
        originating: $scope.filter.originating,
        reviewer: $scope.filter.reviewer
      });
    };

    // Gets the filter state for "Show Matter Involving" and sets it to the filter object to sync the filter state between reports.
    $scope.updateScopeAccount = function () {
      // One write: setFilterState routes scopeAccount through the store action,
      // which is also what records that the user has chosen.
      reportFilterService.setFilterState({
        scopeAccount: $scope.filter.scopeAccount
      });
    };
  }
  function linkVue($scope, element) {
    const store = useReportFiltersStore(getPinia());
    const {
      handleError
    } = useSnackbarHandler();
    let alive = true;
    store.refreshPeriods();
    store.configurePanel({
      family: 'generic',
      show: $scope.show || {},
      accounts: [],
      lockAccountSelection: false,
      distributions: [],
      trustAccounts: []
    });
    $scope.$watch('show', show => store.configurePanel({
      show: show || {}
    }));
    loadPanelContext(store, 'generic', {
      me: () => userService.me(),
      users: () => userService.list({
        status: 'all'
      }),
      distributions: () => leanLawApi.listPaymentDistributions(),
      formatDisabledUser: filterService.formatDisabledUser,
      isAlive: () => alive,
      onError: handleError
    });

    // The engine deep-watches the store's filters, which only runs on a digest.
    const stopWatching = onFiltersChange(store, () => $scope.$applyAsync());
    $scope.$on('$destroy', () => {
      alive = false;
      stopWatching();
    });
    mountReportVue(ReportFilterPanel, $scope, element, $injector);
  }
  return {
    restrict: 'E',
    scope: {
      filter: '=',
      show: '='
    },
    link($scope, element) {
      let alive = true;
      $scope.$on('$destroy', () => {
        alive = false;
      });
      withReportFilterPanelFeature(userService, leanLawAuth.features, () => alive, enabled => {
        if (enabled) {
          linkVue($scope, element);
          return;
        }
        element.html(require('./views/reportFilter.html'));
        $compile(element.contents())($scope);
        linkLegacy($scope);
        $scope.$applyAsync();
      });
    }
  };
}]);